import { Component, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { InterviewService } from '../services/interview.service';
import { CreateInterviewRequest, OpportunityFormData, ResumeData } from '../models/virtual-interview-model';
import { Router } from '@angular/router';

@Component({
  selector: 'app-virtual-interview-steps',
  templateUrl: './virtual-interview-steps.component.html',
  styleUrls: ['./virtual-interview-steps.component.css'],
})
export class VirtualInterviewStepsComponent implements OnInit {
  steps: MenuItem[] = [
    {
      label: 'Upload Resume',
      command: () => this.onStepClick(0),
    },
    {
      label: 'Opportunity Details',
      command: () => this.onStepClick(1),
    },
    {
      label: 'Training Trails',
      command: () => this.onStepClick(2),
    },
  ];

  vrActiveIndex: number = 0;
  isLoading: boolean = true;

  resumeData: ResumeData | null = null;
  opportunityData: OpportunityFormData | null = null;
  createdInterviewId: string | null = null;

  showCompletionMessage: boolean = false;
  submissionError: boolean = false;

  constructor(
    private translate: TranslateService,
    private interviewService: InterviewService,
    private router: Router
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      const activeStep = navigation.extras.state['activeStep'];
      const error = navigation.extras.state['error'];
      
      if (activeStep === 2) {
        this.showCompletionMessage = true;
        this.submissionError = error || false;
      }
    }
  }

  ngOnInit(): void {
    this.checkInterviewStatus();
  }

  checkInterviewStatus(): void {
    this.isLoading = true;

    this.interviewService.getTrainingInterview().subscribe({
      next: (response) => {
        if (response.interviewProfileInfo === null) {
          this.vrActiveIndex = 0;
        } else {
          this.vrActiveIndex = 2;
        }

        if (this.showCompletionMessage && this.vrActiveIndex === 2) {
          setTimeout(() => {
            if (this.submissionError) {
              alert('⚠️ Interview completed but there was an error submitting. Please check your results.');
            } else {
              alert('✅ Interview submitted successfully! Your feedback will be available soon.');
            }
            this.showCompletionMessage = false;
          }, 500);
        }

        this.isLoading = false;
        this.scrollToTop();
      },
      error: (error) => {
        console.error('Error fetching interview status:', error);
        this.vrActiveIndex = 0;
        this.isLoading = false;
      }
    });
  }

  onStepClick(index: number): void {
    this.vrActiveIndex = index;
    this.scrollToTop();
  }

  onResumeNext(data: ResumeData): void {
    this.resumeData = data;
    this.next();
  }

  onOpportunityNext(data: OpportunityFormData): void {
    this.opportunityData = data;
    this.submitInterview();
  }

  submitInterview(): void {
    if (!this.resumeData || !this.opportunityData) {
      alert('Missing required data. Please complete all steps.');
      return;
    }

    this.isLoading = true;

    const requestData: CreateInterviewRequest = {
      jobTitle: this.opportunityData.jobTitle,
      jobDescription: this.opportunityData.jobDescription,
      keywords: this.opportunityData.keywords,
      instructions: [],
      interviewType: 'TRAINING',
      language: this.opportunityData.language
    };

    let cvFile: File | null = null;
    let cvPath: string | undefined = undefined;

    if (this.resumeData.type === 'upload' && this.resumeData.uploadedCV) {
      cvFile = this.resumeData.uploadedCV;
    } else if (this.resumeData.type === 'current' && this.resumeData.selectedCV) {
      cvPath = this.resumeData.selectedCV.imagePath;
      
      if (!cvPath) {
        alert('Selected CV is invalid. Please select a different CV.');
        this.isLoading = false;
        return;
      }
    } else {
      alert('Please upload a CV or select an existing one.');
      this.isLoading = false;
      return;
    }

    console.log('Request data:', {
      ...requestData,
      cvFile: cvFile ? cvFile.name : null,
      cvPath: cvPath
    });

    this.interviewService.createTrainingInterview(requestData, cvFile, cvPath).subscribe({
      next: (response) => {
        this.createdInterviewId = response.interview_id;
        this.isLoading = false;
        this.next();
      },
      error: (error) => {
        console.error('API Error:', error);
        this.isLoading = false;
        
        let errorMessage = 'Failed to create interview. Please try again.';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(errorMessage);
      }
    });
  }

  next(): void {
    if (this.vrActiveIndex < this.steps.length - 1) {
      this.vrActiveIndex++;
      this.scrollToTop();
    }
  }

  previous(): void {
    if (this.vrActiveIndex > 0) {
      this.vrActiveIndex--;
      this.scrollToTop();
    }
  }

  private scrollToTop(): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const content = document.querySelector('#app');
        if (content) {
          content.scrollTop = 0;
        }
      });
    }, 50);
  }
}